import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lock, Undo2, Sparkles, Loader2, ShieldAlert, Info, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge, RefPill } from "@/components/StatusBadge";
import { FieldsGrid } from "@/components/records/DynamicFields";
import { RecordForm } from "@/components/records/RecordForm";
import { WorkflowBar } from "@/components/records/WorkflowBar";
import { AttachmentsPanel } from "@/components/records/AttachmentsPanel";
import { TimelinePanel } from "@/components/records/TimelinePanel";

const Meta = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div><div className="text-sm text-slate-800 mt-0.5">{children}</div></div>
);

const AISummary = ({ id, canApply, onApply }) => {
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState(null);
  const go = async () => { setBusy(true); try { setS((await api.post(`/records/${id}/ai-summary`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="mt-6 pt-5 border-t border-slate-100">
      <Button size="sm" variant="outline" onClick={go} disabled={busy} data-testid="ai-summary-button">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5 text-amber-600" />}AI summary & checks</Button>
      {s && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-4 text-sm space-y-2 fade-up" data-testid="ai-summary-result">
          <p className="text-slate-800">{s.summary}</p>
          {s.tags.length > 0 && <p className="text-xs text-slate-600">Suggested tags: {s.tags.map((t) => `#${t}`).join(" ")}</p>}
          {s.flags.length > 0 && <ul className="text-xs text-amber-800 list-disc pl-4">{s.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>}
          {canApply && <Button size="sm" onClick={() => { onApply(s); setS(null); }} data-testid="ai-summary-apply-button">Use summary & tags</Button>}
        </div>
      )}
    </div>
  );
};

export default function RecordDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { meta } = useAuth();
  const [d, setD] = useState(null);
  const [tl, setTl] = useState([]);
  const [revs, setRevs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, t, v] = await Promise.all([api.get(`/records/${id}`), api.get(`/records/${id}/timeline`), api.get(`/records/${id}/revisions`)]);
      setD(r.data); setTl(t.data); setRevs(v.data);
    } catch (e) { setErr(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setSaving(true);
    try {
      await api.put(`/records/${id}`, { version: d.record.version, title: body.title, fields: body.fields, tags: body.tags, summary: body.summary });
      toast.success("Changes saved"); setEditing(false); load();
    } catch (e) { toast.error(errMsg(e)); if (e.response?.status === 409) load(); } finally { setSaving(false); }
  };
  const applySummary = (s) => save({ ...d.record, summary: s.summary, tags: [...new Set([...d.record.tags, ...s.tags])] });

  if (err) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-700 flex gap-2 items-center" data-testid="record-error"><ShieldAlert className="w-5 h-5" />{err}</div>;
  if (!d) return <div className="h-40 animate-pulse rounded-lg bg-slate-100" />;
  const { record: r, actions, attachments, user_names: names, missing } = d;
  const typeDef = meta.record_types[r.record_type];
  const isApproved = r.status === "approved" || (r.status === "archived" && r.status_before_archive === "approved");
  return (
    <div className="fade-up">
      <div className="flex flex-col xl:flex-row xl:items-start gap-4 justify-between mb-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <RefPill value={r.ref_no} testid="record-ref-no" /><StatusBadge status={r.status} testid="record-status-badge" />
            <span className="text-xs text-slate-500">{typeDef.label}</span>
            <span className="text-[11px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5" data-testid="record-office">{d.office_name}</span>
            {typeDef.sensitive && <span className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">Restricted</span>}
            <span className="text-xs text-slate-500 font-mono">rev {r.revision}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 break-words" data-testid="record-title">{r.title}</h1>
        </div>
        <WorkflowBar record={r} actions={actions} onDone={load} onEdit={() => setEditing(true)} onDeleted={() => nav("/records")} />
      </div>
      {r.locked && <div className="mb-4 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-700" data-testid="record-locked-banner"><Lock className="w-4 h-4" />This record is locked{r.status === "approved" && ` — approved by ${r.approved_by_name} on ${fmtDate(r.approved_at)}`}. Edits require a reopen, which creates a new revision.
        {isApproved && <Button size="sm" variant="outline" asChild className="ml-auto bg-white" data-testid="print-cover-sheet-button"><Link to={`/records/${id}/certificate`}><Printer className="w-4 h-4 mr-1.5" />Cover sheet</Link></Button>}</div>}
      {r.status === "draft" && r.return_comment && <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="record-returned-banner"><Undo2 className="w-4 h-4 mt-0.5" />Returned for correction: {r.return_comment}</div>}
      {r.status === "draft" && actions.submit && missing.length > 0 && <div className="mb-4 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800" data-testid="record-missing-banner"><Info className="w-4 h-4 mt-0.5" />Before submitting, complete: {missing.join(", ")}</div>}
      {editing ? (
        <RecordForm mode="edit" initial={r} attachments={attachments} onSave={save} saving={saving} onCancel={() => setEditing(false)} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          <Tabs defaultValue="details" className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <TabsList className="m-3 bg-slate-100">
              <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
              <TabsTrigger value="attachments" data-testid="tab-attachments">Attachments ({attachments.filter((a) => a.state === "current").length})</TabsTrigger>
              <TabsTrigger value="revisions" data-testid="tab-revisions">Revisions ({revs.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="p-5 pt-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-5 mb-5 border-b border-slate-100">
                <Meta label="Created by">{r.created_by_name}</Meta>
                <Meta label="Created">{fmtDate(r.created_at)}</Meta>
                <Meta label="Approver"><span data-testid="record-assignee">{names[r.assigned_to] || "Unassigned"}</span></Meta>
                <Meta label="Source">{r.source === "public" ? "Public portal" : "Internal"}</Meta>
              </div>
              <FieldsGrid typeDef={typeDef} values={r.fields} />
              {(r.summary || r.tags.length > 0) && <div className="mt-5 pt-5 border-t border-slate-100 space-y-2">
                {r.summary && <p className="text-sm text-slate-700" data-testid="record-summary">{r.summary}</p>}
                <div className="flex flex-wrap gap-1.5">{r.tags.map((t) => <span key={t} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-700">#{t}</span>)}</div>
              </div>}
              {r.decision_comment && <p className="mt-4 text-sm text-slate-600"><b>Decision note:</b> {r.decision_comment}</p>}
              {actions.ai && <AISummary id={id} canApply={actions.edit} onApply={applySummary} />}
            </TabsContent>
            <TabsContent value="attachments" className="p-5 pt-2">
              <AttachmentsPanel recordId={id} attachments={attachments} canEdit={actions.upload} required={typeDef.attachment_required} onChanged={load} />
            </TabsContent>
            <TabsContent value="revisions" className="p-5 pt-2 space-y-3" data-testid="revisions-panel">
              {revs.length === 0 && <p className="text-sm text-slate-500">No earlier revisions. A revision is saved each time a closed record is reopened.</p>}
              {revs.map((v) => (
                <div key={v.revision_id} className="rounded-md border border-slate-200 p-3 text-sm">
                  <div className="font-medium">Revision {v.revision} · <StatusBadge status={v.snapshot.status} /></div>
                  <div className="text-xs text-slate-500 mt-1">Closed {fmtDate(v.closed_at)} · {v.attachments.length} attachment(s) · reopen reason recorded in timeline</div>
                  <div className="text-xs text-slate-600 mt-1 truncate">{v.snapshot.title}</div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
          <TimelinePanel items={tl} />
        </div>
      )}
    </div>
  );
}
